import { Action, createAction, props } from '@ngrx/store';

import {
  IAccount,
  AccountCategory,
  ProAccountsResponse,
  UserInvitation,
  AccountBootstrapPendingPayload,
  AccountCreateRequest,
  AccountsFilter,
  AccountPackageResponse,
  ProAccount,
  LinkProAccountsPayload,
} from '@account-users/account/models';

import { EmailEventDiscriminator, IMigration } from '@shared';

export enum AccountsActionTypes {
  SortAccounts = '[Accounts] Sort Accounts',

  LoadAccounts = '[Accounts] Load Accounts',
  LoadAccountsSuccess = '[Accounts] Load Accounts Success',
  LoadAccountsFailure = '[Accounts] Load Accounts Failure',

  LoadAccountMigrations = '[Accounts] Load Account Migrations',
  LoadAccountMigrationsSuccess = '[Accounts] Load Account Migrations Success',
  LoadAccountMigrationsFailure = '[Accounts] Load Account Migrations Failure',

  LoadAccountCategories = '[Accounts] Load Account Categories',
  LoadAccountCategoriesFailure = '[Accounts] Load Account Categories Failure',
  LoadAccountCategoriesSuccess = '[Accounts] Load Account Categories Success',

  CreateAccount = '[Accounts] Create Account',
  CreateAccountFailure = '[Accounts] Create Account Failure',
  CreateAccountSuccess = '[Accounts] Create Account Success',

  PrepareCreateAccount = '[Accounts] Prepare Create Account',
  PrepareCreateAccountFailure = '[Accounts] Prepare Create Account Failure',

  AccountBootstrapDone = '[Accounts] Account Bootstrap Done',
  AccountBootstrapPending = '[Accounts] Account Bootstrap Pending',
  AccountBootstrapCheckFailed = '[Accounts] Account Bootstrap Check Failed',

  LinkProAccountIds = '[Accounts] Link Pro Account Ids',
  LinkProAccountIdsSuccess = '[Accounts] Link Pro Account Ids Success',
  LinkProAccountIdsFailure = '[Accounts] Link Pro Account Ids Failed',

  InviteAccountAdmin = '[Accounts] Invite Account Admin',
  InviteAccountAdminSuccess = '[Accounts] Invite Account Admin Success',
  InviteAccountAdminFailure = '[Accounts] Invite Account Admin Failure',

  ResetCreateAccountState = '[Accounts] Reset Create Account State',

  DeleteAccount = '[Accounts] Delete Account',
  DeleteAccountSuccess = '[Accounts] Delete Account Success',
  DeleteAccountFailure = '[Accounts] Delete Account Failure',

  ResetUpdateLicensePackage = '[Accounts] Reset Update Account License Package',

  UpdateLicensePackage = '[Accounts] Update Account License Package',
  UpdateLicensePackageSuccess = '[Accounts] Update Account License Package Success',
  UpdateLicensePackageFailure = '[Accounts] Update Account License Package Failure',

  UpdateLicensePackageStatusDone = '[Accounts] Update Account License Package Status Done',

  GetProAccounts = '[Accounts] Get Pro Accounts',
  GetProAccountsSuccess = '[Accounts] Get Pro Accounts Success',
  GetProAccountsFailure = '[Accounts] Get Pro Accounts Failed',

  PutProAccountsSuccess = '[Accounts] Put Pro Accounts Success',
  UnlinkProAccountSuccess = '[Accounts] Unlink Pro Account Success',

  AccountBootstrapEmailEvent = '[Accounts] Account Bootstrap Email Event',
  AccountBootstrapEmailEventSuccess = '[Accounts] Account Bootstrap Email Event Success',
  AccountBootstrapEmailEventFailure = '[Accounts] Account Bootstrap Email Event Failure',

  CreateCrmKeyspace = '[Accounts] Create Crm Keyspace',
  CreateCrmKeyspaceSuccess = '[Accounts] Create Crm Keyspace Success',
  CreateCrmKeyspaceFailure = '[Accounts] Create Crm Keyspace Failure',
  CreateCrmKeyspaceTimeoutFailure = '[Accounts] Create Crm Keyspace 504 Timeout Failure',

  RebootstrapLicenses = '[Accounts] Rebootstrap Licenses',
  RebootstrapLicensesSuccess = '[Accounts] Rebootstrap Licenses Success',
  RebootstrapLicensesFailure = '[Accounts] Rebootstrap Licenses Failure',
  RebootstrapLicensesTimeoutFailure = '[Accounts] Rebootstrap Licenses 504 Timeout Failure',

  AccountLock = '[Accounts] Account Lock',
  AccountLockSuccess = '[Accounts] Account Lock Success',
  AccountLockFailure = '[Accounts] Account Lock Failure',

  AccountActive = '[Accounts] Account Acitve',
  AccountActiveSuccess = '[Accounts] Account Active Success',
  AccountActiveFailure = '[Accounts] Account Active Failure',

  AddEmailQuota = '[Accounts] Add Email Quota',
  AddEmailQuotaSuccess = '[Accounts] Add Email Quota Success',
  AddEmailQuotaFailure = '[Accounts] Add Email Quota Failure',

  AddSmsCredits = '[Accounts] Add Sms Credits',
  AddSmsCreditsSuccess = '[Accounts] Accounts Sms Credits Success',
  AddSmsCreditsFailure = '[Accounts] Accounts Sms Credits Failure',
}

export class SortAccountsAction implements Action {
  readonly type = AccountsActionTypes.SortAccounts;
}

export class LoadAccounts implements Action {
  readonly type = AccountsActionTypes.LoadAccounts;
  constructor(public payload?: AccountsFilter) {}
}

export class LoadAccountsFailure implements Action {
  readonly type = AccountsActionTypes.LoadAccountsFailure;
}

export class LoadAccountsSuccess implements Action {
  readonly type = AccountsActionTypes.LoadAccountsSuccess;
  constructor(public payload: IAccount[]) {}
}

export class LoadAccountMigrations implements Action {
  readonly type = AccountsActionTypes.LoadAccountMigrations;
  constructor(public payload?: { accountId: string }) {}
}

export class LoadAccountMigrationsSuccess implements Action {
  readonly type = AccountsActionTypes.LoadAccountMigrationsSuccess;
  constructor(public payload: { migrations: IMigration[] }) {}
}

export class LoadAccountMigrationsFailure implements Action {
  readonly type = AccountsActionTypes.LoadAccountMigrationsFailure;
}

export class LoadAccountCategories implements Action {
  readonly type = AccountsActionTypes.LoadAccountCategories;
}

export class LoadAccountCategoriesFailure implements Action {
  readonly type = AccountsActionTypes.LoadAccountCategoriesFailure;
}

export class LoadAccountCategoriesSuccess implements Action {
  readonly type = AccountsActionTypes.LoadAccountCategoriesSuccess;
  constructor(public payload: AccountCategory[]) {}
}

export class CreateAccount implements Action {
  readonly type = AccountsActionTypes.CreateAccount;
  constructor(public payload: AccountCreateRequest) {}
}

export class CreateAccountFailure implements Action {
  readonly type = AccountsActionTypes.CreateAccountFailure;
}

export class CreateAccountSuccess implements Action {
  readonly type = AccountsActionTypes.CreateAccountSuccess;
  constructor(public payload: IAccount) {}
}

export class PrepareCreateAccount implements Action {
  readonly type = AccountsActionTypes.PrepareCreateAccount;
  constructor(public payload: AccountCreateRequest) {}
}
export class PrepareCreateAccountFailure implements Action {
  readonly type = AccountsActionTypes.PrepareCreateAccount;
}

export class AccountBootstrapPending implements Action {
  readonly type = AccountsActionTypes.AccountBootstrapPending;
  constructor(public payload: AccountBootstrapPendingPayload) {}
}

export class AccountBootstrapDone implements Action {
  readonly type = AccountsActionTypes.AccountBootstrapDone;
  constructor(public payload: IAccount) {}
}
export class AccountBootstrapCheckFailed implements Action {
  readonly type = AccountsActionTypes.AccountBootstrapCheckFailed;
}

export class LinkProAccountIds implements Action {
  readonly type = AccountsActionTypes.LinkProAccountIds;
  constructor(public payload: LinkProAccountsPayload) {}
}

export class LinkProAccountIdsSuccess implements Action {
  readonly type = AccountsActionTypes.LinkProAccountIdsSuccess;
  constructor(public payload: ProAccountsResponse) {}
}

export class LinkProAccountIdsFailure implements Action {
  readonly type = AccountsActionTypes.LinkProAccountIdsFailure;
}

export class InviteAccountAdmin implements Action {
  readonly type = AccountsActionTypes.InviteAccountAdmin;
  constructor(public payload: UserInvitation) {}
}

export class InviteAccountAdminSuccess implements Action {
  readonly type = AccountsActionTypes.InviteAccountAdminSuccess;
}

export class InviteAccountAdminFailure implements Action {
  readonly type = AccountsActionTypes.InviteAccountAdminFailure;
}

export class ResetCreateAccountState implements Action {
  readonly type = AccountsActionTypes.ResetCreateAccountState;
}

export class DeleteAccount implements Action {
  readonly type = AccountsActionTypes.DeleteAccount;
  constructor(public payload: string) {}
}

export class DeleteAccountSuccess implements Action {
  readonly type = AccountsActionTypes.DeleteAccountSuccess;
  constructor(public payload: string) {}
}

export class DeleteAccountFailure implements Action {
  readonly type = AccountsActionTypes.DeleteAccountFailure;
}

export class ResetUpdateLicensePackage implements Action {
  readonly type = AccountsActionTypes.ResetUpdateLicensePackage;
}

export class UpdateLicensePackage implements Action {
  readonly type = AccountsActionTypes.UpdateLicensePackage;
  constructor(
    public payload: {
      accountId: string;
      orderId: string;
      licensePackage: string;
    },
  ) {}
}

export class UpdateLicensePackageSuccess implements Action {
  readonly type = AccountsActionTypes.UpdateLicensePackageSuccess;
  constructor(
    public payload: {
      accountId: string;
      licensePackage: string;
      licenses: AccountPackageResponse[];
    },
  ) {}
}

export class UpdateLicensePackageFailure implements Action {
  readonly type = AccountsActionTypes.UpdateLicensePackageFailure;
}

export class UpdateLicensePackageStatusDone implements Action {
  readonly type = AccountsActionTypes.UpdateLicensePackageStatusDone;
}

export class GetProAccountsAction implements Action {
  readonly type = AccountsActionTypes.GetProAccounts;
  constructor(public payload: string) {}
}

export class GetProAccountsSuccessAction implements Action {
  readonly type = AccountsActionTypes.GetProAccountsSuccess;
  constructor(public payload: ProAccount[]) {}
}

export class GetProAccountsFailureAction implements Action {
  readonly type = AccountsActionTypes.GetProAccountsFailure;
}

export class PutProAccountsSuccessAction implements Action {
  readonly type = AccountsActionTypes.PutProAccountsSuccess;
  constructor(public payload: ProAccount[]) {}
}

export class UnlinkProAccountSuccessAction implements Action {
  readonly type = AccountsActionTypes.UnlinkProAccountSuccess;
  constructor(public payload: number) {}
}

export class AccountBootstrapEmailEventAction implements Action {
  readonly type = AccountsActionTypes.AccountBootstrapEmailEvent;
  constructor(
    public payload: {
      accountId: string;
      eventDiscriminators: EmailEventDiscriminator[];
    },
  ) {}
}

export class AccountBootstrapEmailEventSuccessAction implements Action {
  readonly type = AccountsActionTypes.AccountBootstrapEmailEventSuccess;
}

export class AccountBootstrapEmailEventFailureAction implements Action {
  readonly type = AccountsActionTypes.AccountBootstrapEmailEventFailure;
}

export class CreateCrmKeyspaceAction implements Action {
  readonly type = AccountsActionTypes.CreateCrmKeyspace;
  constructor(public payload: string) {}
}

export class CreateCrmKeyspaceSuccessAction implements Action {
  readonly type = AccountsActionTypes.CreateCrmKeyspaceSuccess;
}

export class CreateCrmKeyspaceFailureAction implements Action {
  readonly type = AccountsActionTypes.CreateCrmKeyspaceFailure;
}

export class CreateCrmKeyspaceTimeoutFailure implements Action {
  readonly type = AccountsActionTypes.CreateCrmKeyspaceTimeoutFailure;
}

export class RebootstrapLicensesAction implements Action {
  readonly type = AccountsActionTypes.RebootstrapLicenses;
  constructor(public payload: string) {}
}

export class RebootstrapLicensesSuccessAction implements Action {
  readonly type = AccountsActionTypes.RebootstrapLicensesSuccess;
}

export class RebootstrapLicensesFailureAction implements Action {
  readonly type = AccountsActionTypes.RebootstrapLicensesFailure;
}

export class RebootstrapLicensesTimeoutFailureAction implements Action {
  readonly type = AccountsActionTypes.RebootstrapLicensesTimeoutFailure;
}

export class AccountLockAction implements Action {
  readonly type = AccountsActionTypes.AccountLock;
  constructor(public payload: string) {}
}

export class AccountLockSuccessAction implements Action {
  readonly type = AccountsActionTypes.AccountLockSuccess;
}

export class AccountLockFailureAction implements Action {
  readonly type = AccountsActionTypes.AccountLockFailure;
}

export class AccountActiveAction implements Action {
  readonly type = AccountsActionTypes.AccountActive;
  constructor(public payload: string) {}
}

export class AccountActiveSuccessAction implements Action {
  readonly type = AccountsActionTypes.AccountActiveSuccess;
}

export class AccountActiveFailureAction implements Action {
  readonly type = AccountsActionTypes.AccountActiveFailure;
}

export namespace EmailQuota {
  export const request = createAction(
    AccountsActionTypes.AddEmailQuota,
    props<{ accountId: string; emailQuota: number; orderId: string }>(),
  );
  export const success = createAction(AccountsActionTypes.AddEmailQuotaSuccess);
  export const failure = createAction(AccountsActionTypes.AddEmailQuotaFailure);
}

export namespace SmsCredits {
  export const request = createAction(
    AccountsActionTypes.AddSmsCredits,
    props<{ accountId: string; smsCredits: number; orderId: string }>(),
  );
  export const success = createAction(AccountsActionTypes.AddSmsCreditsSuccess);
  export const failure = createAction(AccountsActionTypes.AddEmailQuotaFailure);
}

export type AccountsAction =
  | SortAccountsAction
  | LoadAccountCategoriesFailure
  | LoadAccountCategories
  | LoadAccountCategoriesSuccess
  | LoadAccounts
  | LoadAccountsSuccess
  | LoadAccountsFailure
  | LoadAccountMigrations
  | LoadAccountMigrationsSuccess
  | LoadAccountMigrationsFailure
  | CreateAccount
  | CreateAccountSuccess
  | CreateAccountFailure
  | AccountBootstrapPending
  | AccountBootstrapDone
  | AccountBootstrapCheckFailed
  | LinkProAccountIds
  | LinkProAccountIdsSuccess
  | LinkProAccountIdsFailure
  | InviteAccountAdmin
  | InviteAccountAdminSuccess
  | InviteAccountAdminFailure
  | ResetCreateAccountState
  | DeleteAccount
  | DeleteAccountSuccess
  | DeleteAccountFailure
  | PrepareCreateAccount
  | PrepareCreateAccountFailure
  | ResetUpdateLicensePackage
  | UpdateLicensePackage
  | UpdateLicensePackageSuccess
  | UpdateLicensePackageFailure
  | UpdateLicensePackageStatusDone
  | GetProAccountsAction
  | GetProAccountsSuccessAction
  | GetProAccountsFailureAction
  | PutProAccountsSuccessAction
  | UnlinkProAccountSuccessAction
  | AccountBootstrapEmailEventAction
  | AccountBootstrapEmailEventSuccessAction
  | AccountBootstrapEmailEventFailureAction
  | CreateCrmKeyspaceAction
  | CreateCrmKeyspaceSuccessAction
  | CreateCrmKeyspaceFailureAction
  | CreateCrmKeyspaceTimeoutFailure
  | AccountActiveAction
  | AccountActiveSuccessAction
  | AccountActiveFailureAction
  | AccountLockAction
  | AccountLockSuccessAction
  | AccountLockFailureAction
  | RebootstrapLicensesAction
  | RebootstrapLicensesSuccessAction
  | RebootstrapLicensesFailureAction
  | RebootstrapLicensesTimeoutFailureAction;
